import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileService, networkService, userService, organizationService } from '../services/api';
import '../styles/ProfileSettings.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const isValidHttpUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  try {
    const parsedUrl = new URL(value.trim());
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const normalizeHttpUrl = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
};

const ProfileSettings = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Gestion du défilement vers le haut
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // États pour la suppression du profil
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  
  // État initial avec des valeurs par défaut pour éviter les erreurs
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    pronouns: '',
    email: '',
    phone: '',
    titles: [],
    photo: '',
    expertise: [], // Valeur par défaut pour éviter l'erreur de map()
    networks: [],
    websites: [],
    socialMedia: [],
    languages: [],
    datasets: [],
    projects: [],
    infrastructure: [],
    cv: '',
    documents: []
  });
  
  // Fonction de sécurité pour vérifier si un tableau est valide avant d'utiliser map()
  const safeMap = (array, callback) => {
    if (Array.isArray(array) && array.length > 0) {
      return array.map(callback);
    }
    return null;
  };
  
  // État pour gérer les nouvelles entrées
  const [newExpertise, setNewExpertise] = useState('');
  const [expertiseOptions, setExpertiseOptions] = useState([]);
  const [newNetwork, setNewNetwork] = useState('');
  const [availableNetworks, setAvailableNetworks] = useState([]);
  const [newWebsite, setNewWebsite] = useState({ name: '', url: '' });
  const [newSocialMedia, setNewSocialMedia] = useState({ platform: 'LinkedIn', url: '' });
  const [newLanguage, setNewLanguage] = useState('');
  const [languageSuggestions, setLanguageSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Liste des langues communes par défaut
  const defaultLanguages = [
    'Français', 'Anglais', 'Espagnol', 'Allemand', 'Italien', 'Portugais',
    'Arabe', 'Chinois', 'Japonais', 'Russe', 'Néerlandais', 'Suédois',
    'Norvégien', 'Danois', 'Finnois', 'Polonais', 'Turc', 'Grec', 'Hindi'
  ];
  
  // Filtrer les suggestions de langues
  const filterLanguages = (input) => {
    if (!input) return defaultLanguages;
    return defaultLanguages.filter(lang =>
      lang.toLowerCase().includes(input.toLowerCase()) &&
      !formData.languages?.includes(lang)
    );
  };
  
  // Gérer les changements dans le champ de langue
  const handleLanguageChange = (e) => {
    const value = e.target.value;
    setNewLanguage(value);
    setLanguageSuggestions(filterLanguages(value));
    setShowSuggestions(true);
  };
  
  // Sélectionner une langue depuis les suggestions
  const selectLanguage = (lang) => {
    setNewLanguage(lang);
    setShowSuggestions(false);
  };
  const [newDataset, setNewDataset] = useState({ name: '', url: '', doi: '', fundedByRQM: false });
  const [newProject, setNewProject] = useState({ name: '', description: '', fundedByRQM: false });
  const [newInfrastructure, setNewInfrastructure] = useState({ name: '', url: '' });
  const [newDocument, setNewDocument] = useState({ name: '', url: '', description: '' });
  const [newTitle, setNewTitle] = useState({ title: '', organization: '', department: '', unit: '' });
  const [organizationSuggestions, setOrganizationSuggestions] = useState([]);
  const [showOrgSuggestions, setShowOrgSuggestions] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvUrl, setCvUrl] = useState('');
  const [cvUploadType, setCvUploadType] = useState('url'); // 'url' ou 'file'
  const [documentFile, setDocumentFile] = useState(null);
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentUploadType, setDocumentUploadType] = useState('url'); // 'url' ou 'file'
  
  // Charger les expertises existantes pour l'autosuggestion
  useEffect(() => {
    async function fetchExpertiseOptions() {
      try {
        const data = await profileService.getProfiles();
        // Extraire toutes les expertises uniques
        const allExpertise = data.flatMap(profile => profile.expertise || []);
        const uniqueExpertise = [...new Set(allExpertise.filter(e => e && e.trim() !== ''))];
        setExpertiseOptions(uniqueExpertise);
      } catch (err) {
        console.error('Erreur lors du chargement des expertises pour autosuggestion:', err);
      }
    }
    fetchExpertiseOptions();
  }, []);

  // État pour le chargement des suggestions d'organisations
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [isOrganizationsLoaded, setIsOrganizationsLoaded] = useState(false);
  
  // Référence pour le timer de recherche
  const searchTimerRef = useRef(null);
  
  // Charger les organisations au montage du composant
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await organizationService.getOrganizations();
        setOrganizations(response.data);
        setIsOrganizationsLoaded(true);
      } catch (error) {
        console.error('Erreur lors du chargement des organisations:', error);
        setIsOrganizationsLoaded(true); // Marquer comme chargé même en cas d'erreur
      }
    };
    
    fetchOrganizations();
  }, []);
  // Référence pour le conteneur des suggestions
  const suggestionsRef = useRef(null);

  const loadOrganizationDetails = async (titles = []) => {
    if (!Array.isArray(titles) || titles.length === 0) {
      return [];
    }

    return titles.map((title) => {
      if (!title || typeof title !== 'object') {
        return title;
      }

      const enrichedTitle = { ...title };

      if (title.organization && typeof title.organization === 'object') {
        enrichedTitle.organizationName = title.organization.name || title.organizationName || '';
        enrichedTitle.organization = title.organization._id || title.organization.id || title.organization;
        return enrichedTitle;
      }

      if (typeof title.organization === 'string') {
        const matchingOrganization = organizations.find((organization) => (
          organization._id === title.organization
          || organization.name?.toLowerCase() === title.organization.toLowerCase()
        ));

        if (matchingOrganization) {
          enrichedTitle.organization = matchingOrganization._id;
          enrichedTitle.organizationName = matchingOrganization.name;
          return enrichedTitle;
        }
      }

      if (!enrichedTitle.organizationName && typeof title.organization === 'string') {
        enrichedTitle.organizationName = title.organization;
      }

      return enrichedTitle;
    });
  };

  // Fonction pour rechercher des organisations avec correspondance partielle
  const searchOrganizations = async (query) => {
    if (!query || query.trim() === '') {
      setOrganizationSuggestions([]);
      setShowOrgSuggestions(false);
      return;
    }
    
    setIsLoadingOrgs(true);
    setShowOrgSuggestions(true);
    
    try {
      // Si des organisations sont déjà chargées, effectuer une recherche côté client
      if (organizations.length > 0) {
        const searchTerm = query.toLowerCase();
        const filtered = organizations.filter(org => 
          org.name && org.name.toLowerCase().includes(searchTerm)
        );
        setOrganizationSuggestions(filtered);
      } else {
        // Sinon, faire une requête API
        const response = await organizationService.searchOrganizations(query);
        setOrganizationSuggestions(response.data || []);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'organisations:', error);
      setOrganizationSuggestions([]);
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  // Gestion du changement d'organisation avec délai pour éviter trop de requêtes
  const handleOrganizationChange = (e) => {
    const value = e.target.value;
    setNewTitle(prev => ({ ...prev, organization: value }));
    
    // Afficher les suggestions immédiatement si on a des organisations en cache
    if (organizations.length > 0) {
      if (value) {
        const searchTerm = value.toLowerCase();
        const filtered = organizations.filter(org => 
          org.name && org.name.toLowerCase().includes(searchTerm)
        );
        setOrganizationSuggestions(filtered);
        setShowOrgSuggestions(true);
      } else {
        // Si le champ est vide, afficher toutes les organisations
        setOrganizationSuggestions(organizations);
        setShowOrgSuggestions(true);
      }
    } else {
      // Annuler le timer précédent s'il existe
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      
      // Délai avant de lancer la recherche pour éviter trop de requêtes
      searchTimerRef.current = setTimeout(() => {
        searchOrganizations(value);
      }, 300); // Réduit à 300ms pour une meilleure réactivité
    }
  };
  
  // Sélectionner une suggestion d'organisation
  const selectOrganization = (org) => {
    setNewTitle(prev => ({
      ...prev,
      organization: org.name
    }));
    setShowOrgSuggestions(false);
  };
  
  // Gérer le clic en dehors de la liste déroulante
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowOrgSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Gestion des changements de base
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  // Gestion des changements pour les tableaux simples
  const handleArrayAdd = (field, value, setValue) => {
    const trimmedValue = value.trim();
    if (trimmedValue !== '') {
      const currentArray = Array.isArray(formData?.[field]) ? [...formData[field]] : [];
      // Empêcher l'ajout d'une valeur vide ou d'un doublon
      if (field === 'expertise') {
        if (!trimmedValue) {
          setError('Veuillez entrer une expertise valide.');
          setTimeout(() => setError(null), 3000);
          setValue('');
          return;
        }
        if (currentArray.some(item => item.toLowerCase() === trimmedValue.toLowerCase())) {
          setError('Cette expertise existe déjà dans votre profil.');
          setTimeout(() => setError(null), 3000);
          setValue('');
          return;
        }
      }
      const newArray = [...currentArray, trimmedValue];
      setFormData(prevState => ({
        ...prevState,
        [field]: newArray
      }));
      setValue('');
    }
  };
  
  const handleArrayRemove = (field, index) => {
    // Vérifier que le champ existe et est un tableau
    if (Array.isArray(formData[field])) {
      if (field === 'expertise' && formData[field].length === 1) {
        if (!window.confirm('Voulez-vous vraiment supprimer cette expertise ?')) {
          return;
        }
      }
      // Créer une copie du tableau pour éviter de modifier l'original
      const updatedArray = [...formData[field]].filter((_, i) => i !== index);
      
      // Utiliser la forme fonctionnelle de setFormData pour garantir l'état le plus récent
      setFormData(prevState => ({
        ...prevState,
        [field]: updatedArray
      }));
    } else {
      console.error(`Le champ ${field} n'est pas un tableau ou n'existe pas`);
    }
  };
  
  // Fonction utilitaire pour ajouter des éléments à un tableau d'objets ou simple
  const handleObjectArrayAdd = async (field, value, setValue, emptyValue) => {
    // Vérification en fonction du type de champ
    let isValid = false;
    if (field === 'titles') {
      isValid = value.title && value.title.trim() !== '';
    } else if (field === 'socialMedia') {
      isValid = value.url && value.url.trim() !== '' && value.platform && value.platform.trim() !== '';
    } else if (field === 'projects') {
      isValid = value.name && value.name.trim() !== '';
    } else if (field === 'datasets' || field === 'websites') {
      isValid = value.name && value.name.trim() !== '' && value.url && value.url.trim() !== '';
    } else {
      // Pour les autres champs, au moins un champ doit être rempli
      isValid = Object.values(value).some(val => val && val.toString().trim() !== '');
    }
    
    if (!isValid) {
      setError('Veuillez remplir tous les champs obligatoires');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      let processedValue = { ...value };

      if (field === 'titles' && value.organization) {
        const matchingOrganization = organizations.find(org =>
          org.name && org.name.toLowerCase() === value.organization.trim().toLowerCase()
        );

        if (!matchingOrganization) {
          setError('Veuillez sélectionner une organisation existante dans la liste de suggestions.');
          setTimeout(() => setError(null), 4000);
          return;
        }

        processedValue.organization = {
          _id: matchingOrganization._id,
          name: matchingOrganization.name
        };
      }

      if (['socialMedia', 'websites', 'datasets', 'projects', 'infrastructure'].includes(field) && processedValue.url) {
        processedValue.url = normalizeHttpUrl(processedValue.url);

        if (!isValidHttpUrl(processedValue.url)) {
          setError('Veuillez entrer une URL valide commençant par http:// ou https://');
          setTimeout(() => setError(null), 4000);
          return;
        }
      }

      const updatedArray = [...(formData[field] || []), processedValue];

      setFormData(prevState => ({
        ...prevState,
        [field]: updatedArray
      }));

      setValue(emptyValue);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'élément:', error);
      setError(`Une erreur est survenue lors de l'ajout de l'élément: ${error.message || 'Erreur inconnue'}`);
      setTimeout(() => setError(null), 3000);
    }
  };
  
  const handleObjectArrayRemove = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index)
    });
  };

  const handleAddNetwork = async () => {
    const trimmedNetwork = newNetwork.trim();

    if (!trimmedNetwork) {
      return;
    }

    const existingNetwork = availableNetworks.find(network =>
      network.name && network.name.toLowerCase() === trimmedNetwork.toLowerCase()
    );

    if (existingNetwork) {
      if (formData.networks.includes(existingNetwork._id)) {
        setError('Ce regroupement stratégique est déjà dans votre liste.');
        setTimeout(() => setError(null), 3000);
        return;
      }

      handleArrayAdd('networks', existingNetwork._id, setNewNetwork);
      return;
    }

    setError('Ce regroupement stratégique n’existe pas. Veuillez sélectionner une option existante ou contacter l’administration pour en ajouter un nouveau.');
    setTimeout(() => setError(null), 4000);
  };
  
  // Gestion du téléversement de photo
  const handlePhotoUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        setError('Seules les images sont acceptées');
        setTimeout(() => setError(null), 3000);
        return;
      }
      
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille de l\'image ne doit pas dépasser 5MB');
        setTimeout(() => setError(null), 3000);
        return;
      }
      
      // Créer un aperçu de l'image
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          photo: reader.result
        });
      };
      reader.readAsDataURL(file);
      
      // Téléverser la photo si un profil existe déjà
      if (formData._id) {
        const formDataObj = new FormData();
        formDataObj.append('photo', file);
        
        await profileService.uploadProfilePhoto(formData._id, file);
      }
    } catch (error) {
      console.error('Erreur lors du téléversement de la photo:', error);
      setError('Erreur lors du téléversement de la photo');
      setTimeout(() => setError(null), 3000);
    }
  };
  
  // Gestion du téléversement de CV
  const handleRemoveCV = async () => {
    setError(null);
    setSuccess(false);
    try {
      // Suppression côté backend si possible
      if (formData._id) {
        await profileService.removeCV(formData._id); // Cette méthode doit exister côté backend
      }
      setFormData(prevState => ({
        ...prevState,
        cv: ''
      }));
      toast.success('CV supprimé avec succès.');
    } catch (err) {
      setError('Erreur lors de la suppression du CV. Veuillez réessayer.');
      toast.error('Erreur lors de la suppression du CV.');
    }
  };

  const handleCvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier le type de fichier
    if (file.type !== 'application/pdf') {
      setError('Seuls les fichiers PDF sont acceptés');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    // Vérifier la taille du fichier (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('La taille du fichier ne doit pas dépasser 10MB');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    setCvFile(file);
  };
  
  // Gestion de l'upload du CV
  const handleCvUpload = () => {
    setError(null);
    setSuccess(false);
    
    if (cvUploadType === 'url') {
      if (!cvUrl) {
        setError('Veuillez entrer une URL valide');
        return;
      }
      
      const cvDoc = {
        name: cvUrl.split('/').pop() || 'CV',
        url: cvUrl,
        description: 'CV personnel',
        fileType: 'url'
      };
      
      // Mise à jour immédiate de l'état local
      setFormData(prevState => ({
        ...prevState,
        cv: cvDoc
      }));
      
      // Réinitialiser le champ URL
      setCvUrl('');
      
      // Synchroniser avec le serveur en arrière-plan si un ID de profil existe
      if (formData._id) {
        profileService.updateCVUrl(formData._id, cvDoc.url)
          .then(response => {
            // Mise à jour de l'état local avec la réponse du serveur
            setFormData(prevState => ({
              ...prevState,
              cv: response
            }));
            setSuccess('CV ajouté avec succès');
            fetchProfileData && fetchProfileData();
          })
          .catch(err => {
            console.error('Erreur lors du téléversement du CV:', err);
            setError('Erreur lors du téléversement du CV. Veuillez réessayer.');
          });
      } else {
        setSuccess('CV ajouté avec succès (enregistrement local)');
      }
    } else {
      // Gérer le téléversement de fichier ici si nécessaire
      setError('Type de téléversement non pris en charge');
    }
  };
  
  // Gestion du téléversement de document
  const handleDocumentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type de fichier (PDF uniquement)
      if (file.type !== 'application/pdf') {
        setError('Seuls les fichiers PDF sont acceptés');
        e.target.value = null; // Réinitialiser l'input
        return;
      }
      
      // Vérifier la taille du fichier (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Le fichier est trop volumineux (max 10MB)');
        e.target.value = null; // Réinitialiser l'input
        return;
      }
      
      setDocumentFile(file);
      setError(null);
    }
  };
  
  // Gestion de l'upload du document - version simplifiée comme pour les CV
  const handleDocumentUpload = () => {
    setError(null);
    setSuccess(false);
    
    // Validation du nom du document (obligatoire pour l'ajout de document)
    if (!newDocument.name.trim()) {
      setError('Veuillez entrer un nom pour le document');
      return;
    }
    
    if (documentUploadType === 'url') {
      // Validation de l'URL
      if (!documentUrl) {
        setError('Veuillez entrer une URL valide');
        return;
      }
      
      // Créer un nouveau document directement dans l'état local
      const newDoc = {
        _id: `temp_${Date.now()}`,
        name: newDocument.name,
        url: documentUrl,
        description: newDocument.description || '',
        fileType: 'url',
        lastUpdated: new Date(),
        isLocal: true
      };
      
      // Mettre à jour l'état local immédiatement
      setFormData(prevState => {
        return {
          ...prevState,
          documents: [...(Array.isArray(prevState.documents) ? prevState.documents : []), newDoc]
        };
      });
      
      // Réinitialiser le formulaire
      setNewDocument({ name: '', url: '', description: '' });
      setDocumentUrl('');
      setSuccess('URL du document ajoutée avec succès');
      
      // Synchroniser avec le serveur en arrière-plan
      if (formData._id) {
        profileService.addDocumentUrl({
          name: newDoc.name,
          url: newDoc.url,
          description: newDoc.description
        }, formData._id)
          .then(() => {
          })
          .catch(err => {
            console.error('Erreur lors de la synchronisation du document URL:', err);
          });
      }
    } else {
      // Validation du fichier PDF
      if (!documentFile) {
        setError('Veuillez sélectionner un fichier PDF');
        return;
      }
      
      // Pour les fichiers PDF, nous devons quand même faire l'upload avant
      profileService.uploadDocument(documentFile, newDocument.name, newDocument.description || '', formData._id)
        .then(response => {
          if (!response) {
            console.error('Erreur: Aucune réponse du serveur');
            setError('Erreur lors du téléversement du document: aucune réponse du serveur');
            return;
          }
          
          // Créer un nouvel objet document avec les données du formulaire si nécessaire
          const newDoc = {
            _id: response.data?._id || `temp_${Date.now()}`,
            name: newDocument.name || 'Document sans nom',
            url: response.data?.url || response.data?.path || (documentFile ? URL.createObjectURL(documentFile) : ''),
            description: response.data?.description || newDocument.description || '',
            fileType: 'pdf',
            lastUpdated: response.data?.lastUpdated || new Date().toISOString(),
            isLocal: !response.data?._id
          };

          // Mettre à jour l'état avec le nouveau document
          setFormData(prevState => {
            const currentDocs = Array.isArray(prevState.documents) ? prevState.documents : [];
            const newDocuments = [...currentDocs, newDoc];

            return {
              ...prevState,
              documents: newDocuments
            };
          });

          // Réinitialiser le formulaire
          setNewDocument({ name: '', url: '', description: '' });
          setDocumentFile(null);
          setDocumentUrl('');
          setSuccess('Document téléversé avec succès');
          
          const fileInput = document.getElementById('document-file');
          if (fileInput) fileInput.value = '';
        })
        .catch(err => {
          console.error('Erreur lors du téléversement du document:', err);
          
          // Afficher plus de détails sur l'erreur
          let errorMessage = 'Erreur lors du téléversement du document. ';
          
          if (err.response) {
            // La requête a été faite et le serveur a répondu avec un statut hors 2xx
            console.error('Détails de l\'erreur:', {
              status: err.response.status,
              statusText: err.response.statusText,
              data: err.response.data,
              headers: err.response.headers
            });
            
            if (err.response.status === 413) {
              errorMessage += 'Le fichier est trop volumineux. La taille maximale est de 10 Mo.';
            } else if (err.response.data && err.response.data.message) {
              errorMessage += err.response.data.message;
            } else {
              errorMessage += `Erreur serveur (${err.response.status}): ${err.response.statusText}`;
            }
          } else if (err.request) {
            // La requête a été faite mais aucune réponse n'a été reçue
            console.error('Aucune réponse du serveur:', err.request);
            errorMessage += 'Le serveur ne répond pas. Vérifiez votre connexion internet.';
          } else {
            // Une erreur s'est produite lors de la configuration de la requête
            console.error('Erreur de configuration de la requête:', err.message);
            errorMessage += 'Erreur de configuration de la requête.';
          }
          
          setError(errorMessage);
        });
    }
  };
  
  // Gestion de la suppression d'un document - version simplifiée
  const handleDocumentDelete = (documentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      // Supprimer immédiatement de l'état local
      setFormData(prevState => ({
        ...prevState,
        documents: prevState.documents.filter(doc => doc._id !== documentId)
      }));
      
      // Si c'est un ID temporaire (commence par 'temp_'), pas besoin d'appeler le serveur
      if (documentId.toString().startsWith('temp_')) {
        setSuccess('Document supprimé avec succès');
        return;
      }
      
      // Pour les documents déjà enregistrés sur le serveur, synchroniser en arrière-plan
      if (formData._id) {
        profileService.deleteDocument(documentId, formData._id)
          .then(() => {
          })
          .catch(err => {
            console.error('Erreur lors de la suppression du document sur le serveur:', err);
          });
      }
      
      setSuccess('Document supprimé avec succès');
    }
  };
  
  // Fonction pour ouvrir la boîte de dialogue de confirmation de suppression
  const openDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  // Fonction pour fermer la boîte de dialogue de confirmation de suppression
  const closeDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
    setDeleteError(null);
  };

  // Fonction pour supprimer complètement les données de l'utilisateur (conformité Loi 25)
  const handleDeleteUserCompleteData = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      
      // Déterminer l'ID de l'utilisateur
      const userId = currentUser?.id;
      
      if (!userId) {
        setDeleteError('Vous devez être connecté pour supprimer vos données');
        setDeleteLoading(false);
        return;
      }
      
      // Appel au service pour supprimer complètement les données
      await userService.deleteUserCompleteData(userId);
      
      // Rediriger vers la page d'accueil après la suppression
      navigate('/', { 
        state: { 
          message: 'Toutes vos données ont été supprimées conformément à la Loi 25 du Québec. Votre session a été terminée.' 
        } 
      });
      
    } catch (err) {
      console.error('Erreur lors de la suppression complète des données:', err);
      setDeleteError('Une erreur est survenue lors de la suppression des données. Veuillez réessayer plus tard.');
      setDeleteLoading(false);
    }
  };
  
  // Récupérer la liste des réseaux disponibles
  const fetchNetworks = async () => {
    try {
      const networks = await networkService.getNetworks();
      setAvailableNetworks(networks);
    } catch (error) {
      console.error('Erreur lors de la récupération des réseaux:', error);
      // Ne pas afficher d'erreur à l'utilisateur, simplement logger l'erreur
    }
  };
  
  // Fonction pour récupérer les données du profil
  const fetchProfileData = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Utiliser l'ID de l'utilisateur pour récupérer son profil
      const userId = currentUser.id || currentUser._id;
      
      try {
        const profileData = await profileService.getProfileByUserId(userId);
        
        // Préparer les données du formulaire, que le profil existe ou non
        let formDataToSet = {};
        let expertiseArray = []; // Valeur par défaut
        
        if (profileData) {
          // Si le profil existe, utiliser ses données
          formDataToSet = {
            ...profileData, // Conserve les autres champs de profileData
            // Gère explicitement firstName, lastName, et email pour la possible imbrication
            // et utilise currentUser comme fallback si les champs sont absents de profileData
            firstName: profileData.user?.firstName || profileData.firstName || currentUser?.firstName || '',
            lastName: profileData.user?.lastName || profileData.lastName || currentUser?.lastName || '',
            email: profileData.user?.email || profileData.email || currentUser?.email || ''
          };
          
          // S'assurer que expertise est bien un tableau et contient toutes les expertises
          if (Array.isArray(profileData.expertise) && profileData.expertise.length > 0) {
            expertiseArray = [...profileData.expertise];
          } else if (typeof profileData.expertise === 'string' && profileData.expertise.trim() !== '') {
            expertiseArray = [profileData.expertise];
          }
          
          // Mettre à jour l'état avec les données du profil
          setFormData({
            ...formDataToSet, // formDataToSet contient maintenant le nom et l'email correctement sourcés
            expertise: expertiseArray
          });
        } else {
          // Si le profil n'existe pas, créer un nouveau profil avec les valeurs par défaut
          const newProfileData = {
            firstName: currentUser.firstName || '',
            lastName: currentUser.lastName || '',
            email: currentUser.email || '',
            pronouns: '',
            phone: '',
            titles: [],
            photo: '',
            expertise: [],
            networks: [],
            websites: [],
            socialMedia: [],
            languages: [],
            datasets: [],
            projects: [],
            infrastructure: [],
            documents: [],
            user: userId
          };
          
          // Mettre à jour le formulaire avec les données de base
          setFormData(newProfileData);
          
          // Créer le profil dans la base de données
          try {
            const createdProfile = await profileService.createOrUpdateProfile(newProfileData);
            
            // Mettre à jour le formulaire avec les données du profil créé
            if (createdProfile && createdProfile._id) {
              setFormData({
                ...newProfileData,
                _id: createdProfile._id
              });
            }
          } catch (createErr) {
            console.error('Erreur lors de la création du profil:', createErr);
            setError('Impossible de créer votre profil. Veuillez réessayer plus tard.');
          }
        }
      } catch (profileErr) {
        console.error('Erreur lors de la récupération du profil:', profileErr);
        setError('Erreur lors du chargement du profil. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur générale lors du chargement du profil:', error);
      setError('Erreur lors du chargement du profil. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  // Surveiller les changements de formData.documents
  // Charger les données du profil au chargement du composant
  useEffect(() => {
    // Créer une fonction asynchrone à l'intérieur du useEffect
    const loadProfileData = async () => {
      try {
        setError(null);
        setLoading(true);

        if (!currentUser) {
          return;
        }
        
        // Utiliser l'ID de l'utilisateur pour récupérer son profil
        const userId = currentUser.id || currentUser._id;

        try {
          const profileData = await profileService.getProfileByUserId(userId);
          
          // Préparer les données du formulaire
          let formDataToSet = {};
          let expertiseArray = []; // Valeur par défaut
          
          if (profileData) {
            // Si le profil existe, utiliser ses données
            formDataToSet = {
              ...profileData, // Conserve les autres champs de profileData
              // Gère explicitement firstName, lastName, et email pour la possible imbrication
              // et utilise currentUser comme fallback si les champs sont absents de profileData
              firstName: profileData.user?.firstName || profileData.firstName || currentUser?.firstName || '',
              lastName: profileData.user?.lastName || profileData.lastName || currentUser?.lastName || '',
              email: profileData.user?.email || profileData.email || currentUser?.email || ''
            };
            
            // Charger les détails des organisations pour les titres
            if (profileData.titles && profileData.titles.length > 0) {
              formDataToSet.titles = await loadOrganizationDetails(profileData.titles);
            }
            
            // S'assurer que expertise est bien un tableau
            if (Array.isArray(profileData.expertise) && profileData.expertise.length > 0) {
              expertiseArray = [...profileData.expertise];
            } else if (typeof profileData.expertise === 'string' && profileData.expertise.trim() !== '') {
              expertiseArray = [profileData.expertise];
            }
          } else {
            // Si le profil n'existe pas, créer un nouveau profil avec les valeurs par défaut
            const newProfileData = {
              firstName: currentUser?.firstName || '',
              lastName: currentUser?.lastName || '',
              email: currentUser?.email || '',
              pronouns: '',
              phone: '',
              titles: [],
              photo: '',
              expertise: [],
              networks: [],
              websites: [],
              socialMedia: [],
              languages: [],
              datasets: [],
              projects: [],
              infrastructure: [],
              documents: []
            };
            
            // Mettre à jour le formulaire avec les données de base
            setFormData(newProfileData);
            
            // Créer le profil dans la base de données
            try {
              const createdProfile = await profileService.createOrUpdateProfile(newProfileData);
              
              // Mettre à jour le formulaire avec les données du profil créé
              if (createdProfile && createdProfile._id) {
                setFormData({
                  ...newProfileData,
                  _id: createdProfile._id
                });
              }
            } catch (createErr) {
              console.error('Erreur lors de la création du profil:', createErr);
              setError('Impossible de créer votre profil. Veuillez réessayer plus tard.');
            }
          }
          
          // Mettre à jour le formulaire avec les données préparées
          setFormData({
            ...formDataToSet, // formDataToSet contient maintenant le nom et l'email correctement sourcés
            expertise: expertiseArray
          });
        } catch (profileErr) {
          console.error('Erreur lors de la récupération du profil:', profileErr);
          setError('Erreur lors du chargement du profil. Veuillez réessayer.');
        }
      } catch (error) {
        console.error('Erreur générale lors du chargement du profil:', error);
        setError('Erreur lors du chargement du profil. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };
    
    // Appeler la fonction asynchrone
    if (currentUser) {
      loadProfileData();
    }
    
    // Vérifier l'état du formData après le rendu initial
    return () => {
      console.log('Nettoyage du useEffect, dernier état du formData:', formData);
    };
  }, [currentUser, navigate]);
  
  useEffect(() => {
    fetchProfileData();
    fetchNetworks();
  }, [currentUser]);
  
  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      // Vérifier que l'utilisateur est connecté
      if (!currentUser) {
        console.error('Erreur: Utilisateur non connecté');
        throw new Error('Vous devez être connecté pour mettre à jour votre profil');
      }
      
      // Vérifier que les organisations sont chargées
      if (!isOrganizationsLoaded) {
        // Attendre un court instant et réessayer
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!isOrganizationsLoaded) {
          throw new Error('Les organisations ne sont pas encore chargées. Veuillez réessayer dans quelques instants.');
        }
      }
      
      // Préparer les titres pour l'envoi au serveur
      let preparedTitles = [];

      if (Array.isArray(formData.titles)) {
        preparedTitles = formData.titles.map(title => {
          if (!title) return null;

          // Créer un nouvel objet avec uniquement les champs nécessaires
          const processedTitle = {
            title: title.title || '',
            department: title.department || '',
            unit: title.unit || ''
          };
          
          // Gérer l'organisation
          if (title.organization) {
            if (typeof title.organization === 'object') {
              // Si c'est un objet, extraire l'ID et le nom
              processedTitle.organization = title.organization._id || title.organization.id;
              processedTitle.organizationName = title.organization.name || '';
            } else if (typeof title.organization === 'string') {
              const org = organizations.find(org =>
                org._id === title.organization || org.name?.toLowerCase() === title.organization.toLowerCase()
              );
              if (org) {
                processedTitle.organization = org._id;
                processedTitle.organizationName = org.name;
              } else {
                throw new Error(`L'organisation "${title.organization}" doit être sélectionnée parmi les organisations existantes.`);
              }
            }
          }

          return processedTitle;
        }).filter(Boolean); // Filtrer les valeurs null/undefined
      }

      const profileDataToSend = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || currentUser.email,
        phone: formData.phone || '',
        expertise: formData.expertise,
        // Inclure les documents existants
        documents: Array.isArray(formData.documents) ? formData.documents : [],
        titles: preparedTitles
      };
      
      // Préparer les documents pour l'envoi
      if (Array.isArray(profileDataToSend.documents) && profileDataToSend.documents.length > 0) {
        // Séparer les documents existants des nouveaux
        const { existingDocs, newDocs } = profileDataToSend.documents.reduce((acc, doc) => {
          if (!doc) return acc;
          
          if (doc._id && doc._id.toString().startsWith('temp_')) {
            acc.newDocs.push({
              name: doc.name,
              url: doc.url,
              description: doc.description || '',
              fileType: doc.fileType || 'pdf'
            });
          } else if (doc._id) {
            acc.existingDocs.push(doc._id);
          }
          
          return acc;
        }, { existingDocs: [], newDocs: [] });
        
        // Mettre à jour les documents avec les IDs existants et les nouveaux documents
        profileDataToSend.documents = [...existingDocs, ...newDocs];
      }
      
      // Ajouter d'autres champs si présents
      if (formData.pronouns) profileDataToSend.pronouns = formData.pronouns;
      if (formData.photo && typeof formData.photo === 'string' && !formData.photo.startsWith('data:')) {
        profileDataToSend.photo = formData.photo;
      }
      
      // Gérer les tableaux de manière à éviter les références circulaires
      const fieldsToCopy = [
        'titles', 'networks', 'websites', 'socialMedia', 
        'languages', 'datasets', 'projects', 'infrastructure'
      ];
      
      fieldsToCopy.forEach(field => {
        if (Array.isArray(formData[field])) {
          // Créer une copie profonde du tableau pour éviter les problèmes de référence
          profileDataToSend[field] = JSON.parse(JSON.stringify(formData[field]));
        }
      });
      
      if (formData.cv) profileDataToSend.cv = formData.cv;

      const urlFieldConfigs = [
        { field: 'websites', label: 'les sites web', required: true },
        { field: 'socialMedia', label: 'les réseaux sociaux', required: true },
        { field: 'datasets', label: 'les jeux de données', required: false },
        { field: 'projects', label: 'les projets', required: false },
        { field: 'infrastructure', label: 'les infrastructures', required: false },
        { field: 'documents', label: 'les documents externes', required: true, filter: (item) => item?.fileType === 'url' || (!item?.fileType && item?.url) }
      ];

      for (const config of urlFieldConfigs) {
        const items = Array.isArray(profileDataToSend[config.field]) ? profileDataToSend[config.field] : [];
        const relevantItems = config.filter ? items.filter(config.filter) : items;

        for (const item of relevantItems) {
          if (!item || typeof item !== 'object') {
            continue;
          }

          if (!item.url) {
            if (config.required) {
              throw new Error(`Veuillez saisir une URL valide pour ${config.label}.`);
            }
            continue;
          }

          item.url = normalizeHttpUrl(item.url);

          if (!isValidHttpUrl(item.url)) {
            throw new Error(`Veuillez saisir une URL valide pour ${config.label}.`);
          }
        }
      }
      
      // Mise à jour du profil via l'API
      try {
        // Préparer les données pour l'envoi
        const dataToSend = {
          ...profileDataToSend,
          // S'assurer que l'expertise est un tableau, même vide
          expertise: Array.isArray(profileDataToSend.expertise) 
            ? profileDataToSend.expertise 
            : (profileDataToSend.expertise ? [profileDataToSend.expertise] : []),
          // S'assurer que les langues sont toujours envoyées comme un tableau de chaînes non vides
          languages: Array.isArray(profileDataToSend.languages)
            ? profileDataToSend.languages
                .filter(language => typeof language === 'string')
                .map(language => language.trim())
                .filter(Boolean)
            : (typeof profileDataToSend.languages === 'string' && profileDataToSend.languages.trim()
                ? [profileDataToSend.languages.trim()]
                : []),
          // Nettoyer les titres
          titles: Array.isArray(profileDataToSend.titles) 
            ? profileDataToSend.titles
                .filter(Boolean)
                .map(({ title, organization, department = '', unit = '' }) => ({
                  title,
                  organization: organization?._id || organization || null,
                  department,
                  unit
                }))
            : []
        };
        
        // Supprimer les champs vides ou inutiles
        Object.keys(dataToSend).forEach(key => {
          if (dataToSend[key] === undefined || dataToSend[key] === '') {
            delete dataToSend[key];
          }
        });

        const updatedProfile = await profileService.createOrUpdateProfile(dataToSend);

// Mettre à jour l'état local avec les données du serveur
       if (updatedProfile) {
            // Enrichir les titres avec les détails d'organisation
            let updatedTitles = updatedProfile.titles;
            if (updatedTitles && updatedTitles.length > 0) {
              updatedTitles = await loadOrganizationDetails(updatedTitles);
            }
            setFormData(prevState => ({
              ...prevState,
              ...updatedProfile,
              // On force le mapping pour les titres enrichis
              titles: updatedTitles,
              // S'assurer que les champs utilisateur sont bien peuplés
              firstName: updatedProfile.user?.firstName || updatedProfile.firstName || prevState.firstName || '',
              lastName: updatedProfile.user?.lastName || updatedProfile.lastName || prevState.lastName || '',
              email: updatedProfile.user?.email || updatedProfile.email || prevState.email || '',
              expertise: updatedProfile.expertise || prevState.expertise,
              networks: updatedProfile.networks || prevState.networks,
              websites: updatedProfile.websites || prevState.websites,
              socialMedia: updatedProfile.socialMedia || prevState.socialMedia,
              languages: updatedProfile.languages || prevState.languages,
              datasets: updatedProfile.datasets || prevState.datasets,
              projects: updatedProfile.projects || prevState.projects,
              infrastructure: updatedProfile.infrastructure || prevState.infrastructure,
              documents: updatedProfile.documents || prevState.documents
            }));
          }

        toast.success('Profil mis à jour avec succès !');
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000); // Masquer le message après 3 secondes
      } catch (apiError) {
        console.error('Erreur API spécifique:', apiError);
        if (typeof apiError === 'object' && apiError !== null) {
          // Extraire le message d'erreur de l'objet d'erreur
          if (apiError.errors && Array.isArray(apiError.errors)) {
            // Cas où l'API renvoie un tableau d'erreurs de validation
            const errorMessages = apiError.errors.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
            throw new Error(`Erreurs de validation: ${errorMessages}`);
          } else if (apiError.msg || apiError.message) {
            throw new Error(apiError.msg || apiError.message);
          } else {
            throw new Error(JSON.stringify(apiError));
          }
        } else {
          throw apiError; // Relancer l'erreur si ce n'est pas un objet
        }
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      
      // Afficher plus de détails sur l'erreur
      let errorDetails = '';
      
      if (err.response) {
        // La requête a été faite et le serveur a répondu avec un statut hors 2xx
        console.error('Réponse du serveur:', err.response.status, err.response.data);
        console.error('Headers:', err.response.headers);
        
        if (err.response.data) {
          if (err.response.data.message) {
            errorDetails = err.response.data.message;
          } else if (err.response.data.error) {
            errorDetails = err.response.data.error;
          } else if (err.response.data.errors) {
            // Si c'est une erreur de validation, afficher tous les messages d'erreur
            errorDetails = Object.values(err.response.data.errors)
              .map(e => e.message || e.msg || JSON.stringify(e))
              .join('\n');
          } else {
            errorDetails = JSON.stringify(err.response.data, null, 2);
          }
        }
        
        errorDetails = `Erreur ${err.response.status}: ${errorDetails || 'Erreur inconnue du serveur'}`;
      } else if (err.request) {
        // La requête a été faite mais aucune réponse n'a été reçue
        console.error('Aucune réponse du serveur:', err.request);
        errorDetails = 'Le serveur ne répond pas. Vérifiez votre connexion internet ou réessayez plus tard.';
      } else {
        // Une erreur s'est produite lors de la configuration de la requête
        console.error('Erreur de configuration:', err.message);
        errorDetails = `Erreur de configuration: ${err.message}`;
      }
      
      const errorMessage = `Impossible de mettre à jour le profil.\n\n${errorDetails}`;
      console.error('Détails complets de l\'erreur:', errorMessage);
      
      toast.error(errorMessage);
    setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !formData.firstName) {
    return (
      <div className="profile-settings-page">
        <div className="container">
          <h1 className="page-title">Paramètres du profil</h1>
          <div className="loading-indicator">Chargement des données...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="profile-settings-page">
      <div className="container">
        <h1 className="page-title">Paramètres du profil</h1>
        
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}
        
        {success && (
          <div className="alert alert-success">Profil mis à jour avec succès!</div>
        )}
        
        {/* Barre de navigation avec raccourcis vers les sections */}
        <div className="profile-nav-container">
          <div className="profile-nav">
            <a href="#personal-info" className="profile-nav-item">Informations personnelles</a>
            <a href="#titles" className="profile-nav-item">Titres et organisations</a>
            <a href="#photo" className="profile-nav-item">Photo</a>
            <a href="#expertise" className="profile-nav-item">Expertises</a>
            <a href="#languages" className="profile-nav-item">Langues</a>
            <a href="#networks" className="profile-nav-item">Regroupement</a>
            <a href="#social" className="profile-nav-item">Réseaux sociaux</a>
            <a href="#projects" className="profile-nav-item">Projets</a>
            <a href="#datasets" className="profile-nav-item">Jeux de données</a>
            <a href="#websites" className="profile-nav-item">Sites web</a>
            <a href="#infrastructure" className="profile-nav-item">Infrastructures</a>
            <a href="#cv" className="profile-nav-item">CV</a>
            <a href="#documents" className="profile-nav-item">Documents</a>
          </div>
        </div>
        
        <form className="profile-form" onSubmit={handleSubmit}>
          <div id="personal-info" className="form-section">
            <h2 className="section-title">Informations personnelles</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">Prénom*</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-control"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="lastName" className="form-label">Nom*</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-control"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="pronouns" className="form-label">Pronoms</label>
              <input
                type="text"
                id="pronouns"
                name="pronouns"
                className="form-control"
                value={formData.pronouns}
                onChange={handleChange}
                placeholder="ex: il/lui, elle/elle"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email" className="form-label">Courriel*</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                value={formData.user?.email || formData.email || ''}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone" className="form-label">Téléphone</label>
              <input
                type="text"
                id="phone"
                name="phone"
                className="form-control"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div id="titles" className="form-section">
            <h2 className="section-title">Titres et organisations</h2>
            
            <div className="array-items">
              {formData?.titles && formData.titles.length > 0 ? (
                formData.titles.map((title, index) => {
                  if (!title) return null;
                  
                  // Gérer différents formats d'organisation
                  let orgName = '';
                  if (title.organization) {
                    if (typeof title.organization === 'object') {
                      // Cas 1: C'est un objet avec _id et name
                      orgName = title.organization.name || '';
                    } else if (typeof title.organization === 'string') {
                      // Cas 2: C'est un ID ou un nom
                      // Vérifier si c'est un ID valide (format MongoDB)
                      const isMongoId = /^[0-9a-fA-F]{24}$/.test(title.organization);
                      
                      if (isMongoId) {
                        // C'est un ID, on essaie de trouver le nom dans la liste des organisations
                        const org = organizations.find(org => org._id === title.organization);
                        orgName = org ? org.name : title.organization;
                      } else {
                        // C'est probablement déjà un nom
                        orgName = title.organization;
                      }
                    }
                  }
                  
                  return (
                    <div key={`${index}-${title.title || 'no-title'}`} className="array-item">
                      <div className="item-content">
                        <strong>{title.title || 'Sans titre'}</strong>
                        {orgName && <span> - {orgName}</span>}
                        {title.department && <span>, {title.department}</span>}
                        {title.unit && <span> ({title.unit})</span>}
                      </div>
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => handleArrayRemove('titles', index)}
                        aria-label="Supprimer ce titre"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="empty-message">Aucun titre ajouté</div>
              )}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Titre"
                    value={newTitle.title}
                    onChange={(e) => setNewTitle({...newTitle, title: e.target.value})}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <div className="position-relative" ref={suggestionsRef}>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nom de l'organisation (optionnel)"
                        value={newTitle.organization || ''}
                        onChange={handleOrganizationChange}
                        onFocus={() => {
                          if (organizations.length > 0) {
                            if (newTitle.organization) {
                              searchOrganizations(newTitle.organization);
                            } else {
                              setOrganizationSuggestions(organizations);
                              setShowOrgSuggestions(true);
                            }
                          }
                        }}
                        onClick={() => {
                          if (organizations.length > 0 && !showOrgSuggestions) {
                            setOrganizationSuggestions(organizations);
                            setShowOrgSuggestions(true);
                          }
                        }}
                        aria-autocomplete="list"
                        aria-expanded={showOrgSuggestions}
                        aria-controls="org-suggestions"
                      />
                      {isLoadingOrgs && (
                        <span className="input-group-text">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Chargement...</span>
                          </div>
                        </span>
                      )}
                    </div>
                    
                    {showOrgSuggestions && (
                      <div 
                        id="org-suggestions"
                        className={`suggestions-dropdown ${isLoadingOrgs ? 'loading' : ''}`}
                        role="listbox"
                      >
                        {isLoadingOrgs ? (
                          <div className="suggestion-item text-muted">
                            Recherche en cours...
                          </div>
                        ) : organizationSuggestions && organizationSuggestions.length > 0 ? (
                          organizationSuggestions.map((org) => (
                            <div 
                              key={org._id}
                              className="suggestion-item"
                              role="option"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectOrganization(org);
                              }}
                            >
                              <div className="suggestion-item-content">
                                <div className="suggestion-title">{org.name}</div>
                                {org.description && (
                                  <div className="suggestion-description text-muted">
                                    {org.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="suggestion-item text-muted">
                            Aucune organisation trouvée
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Département/Faculté (optionnel)"
                    value={newTitle.department}
                    onChange={(e) => setNewTitle({...newTitle, department: e.target.value})}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Unité (optionnel)"
                    value={newTitle.unit}
                    onChange={(e) => setNewTitle({...newTitle, unit: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => handleObjectArrayAdd('titles', newTitle, setNewTitle, { title: '', organization: '', department: '', unit: '' })}
              >
                <i className="fas fa-plus"></i> Ajouter un titre
              </button>
            </div>
          </div>
          
          <div id="photo" className="form-section">
            <h2 className="section-title">Photo de profil</h2>
            
            <div className="photo-upload">
              <div className="current-photo">
                <img src={formData.photo && formData.photo.trim() ? formData.photo : '/images/placeholder-user.jpg'} alt="Photo de profil" />
              </div>
              
              <div className="upload-controls">
                {/* L'input est visible mais caché visuellement, accessible au clavier et au clic via le label */}
                <input
                  type="file"
                  id="photo"
                  name="photo"
                  accept="image/*"
                  className="file-input-accessible"
                  onChange={handlePhotoUpload}
                  tabIndex="0"
                  aria-label="Choisir une photo de profil"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    zIndex: 2,
                    cursor: 'pointer',
                  }}
                />
                <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                  <label htmlFor="photo" className="btn btn-outline" tabIndex="0" style={{ cursor: 'pointer', position: 'relative', zIndex: 1 }}>
                    <i className="fas fa-upload"></i> Téléverser une photo
                  </label>
                </div>
                {/* Texte d'aide */}
                {!formData.photo && (
                  <div className="help-text" style={{ fontSize: '0.95em', color: '#666', marginTop: '0.5em' }}>
                    Aucun fichier sélectionné. Cliquez sur le bouton pour choisir une image (JPEG, PNG, max 5MB).
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div id="expertise" className="form-section">
            <h2 className="section-title">Expertises</h2>
            
            <div className="array-items">
              {Array.isArray(formData?.expertise) && formData.expertise.length > 0 ? (
  formData.expertise.filter(item => item && item.trim() !== '').map((item, index) => (
    <div key={`expertise-${index}-${item}`} className="array-item">
      <div className="item-content">{item}</div>
      <button
        type="button"
        className="btn-remove"
        onClick={() => {
          if (!formData || !Array.isArray(formData.expertise)) {
            console.error('Erreur: formData.expertise n\'est pas un tableau valide', formData);
            return;
          }
          // Permettre la suppression de la dernière expertise mais demander une confirmation
          if (formData.expertise.length === 1) {
            if (window.confirm('Êtes-vous sûr de vouloir supprimer cette expertise ?')) {
              handleArrayRemove('expertise', index);
            }
          } else {
            handleArrayRemove('expertise', index);
          }
        }}
        aria-label={`Supprimer l'expertise ${item}`}
      >
        <i className="fas fa-times"></i>
      </button>
    </div>
  ))
) : (
  <div className="empty-message">Aucune expertise ajoutée</div>
)}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Nouvelle expertise"
                    value={newExpertise}
                    onChange={(e) => setNewExpertise(e.target.value)}
                    className="form-control"
                    list="expertise-suggestions"
                    autoComplete="off"
                  />
                  <datalist id="expertise-suggestions">
                    {expertiseOptions.map((option, idx) => (
                      <option key={option + idx} value={option} />
                    ))}
                  </datalist>
                </div>
                
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => handleArrayAdd('expertise', newExpertise, setNewExpertise)}
                >
                  <i className="fas fa-plus"></i> Ajouter
                </button>
              </div>
            </div>
          </div>
          
          <div id="languages" className="form-section">
            <h2 className="section-title">Langues</h2>
            
            <div className="array-items">
              {safeMap(formData?.languages, (item, index) => (
                <div key={`language-${index}-${item}`} className="array-item">
                  <div className="item-content">{item}</div>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleArrayRemove('languages', index)}
                    aria-label={`Supprimer la langue ${item}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )) || <div className="empty-message">Aucune langue ajoutée</div>}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group" style={{position: 'relative', width: '100%'}}>
                  <input
                    type="text"
                    placeholder="Commencez à taper une langue (ex: Français, Anglais, etc.)"
                    value={newLanguage}
                    onChange={handleLanguageChange}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="form-control"
                    autoComplete="off"
                  />
                  {showSuggestions && languageSuggestions.length > 0 && (
                    <ul className="suggestions-dropdown" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 10,
                      backgroundColor: 'white',
                      border: '1px solid #ced4da',
                      borderRadius: '0.25rem',
                      marginTop: '0.25rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      {languageSuggestions.map((lang, index) => (
                        <li
                          key={index}
                          onClick={() => selectLanguage(lang)}
                          style={{
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                            transition: 'background-color 0.2s',
                            ':hover': {
                              backgroundColor: '#e9ecef'
                            }
                          }}
                          onMouseDown={(e) => e.preventDefault()} // Empêcher le blur de l'input
                        >
                          {lang}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    if (newLanguage.trim()) {
                      handleArrayAdd('languages', newLanguage.trim(), setNewLanguage);
                      setShowSuggestions(false);
                    }
                  }}
                  disabled={!newLanguage.trim()}
                >
                  <i className="fas fa-plus"></i> Ajouter
                </button>
              </div>
              <div className="mt-2 text-muted small">
                Langues suggérées: Français, Anglais, Espagnol, Allemand, Italien, etc.
              </div>
            </div>
          </div>
          
          <div id="networks" className="form-section">
            <h2 className="section-title">Regroupements stratégiques</h2>
            <p className="section-description">Sélectionnez les regroupements stratégiques auxquels vous appartenez.</p>
            
            <div className="array-items">
              {safeMap(formData?.networks, (networkId, index) => {
                // Trouver le réseau correspondant dans la liste des réseaux disponibles
                const networkDetails = availableNetworks.find(n => n._id === networkId) || { name: networkId };
                return (
                  <div key={`network-${index}-${networkId}`} className="array-item">
                    <div className="item-content">{networkDetails.name}</div>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleArrayRemove('networks', index)}
                      aria-label={`Supprimer le réseau ${networkDetails.name}`}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                );
              }) || <div className="empty-message">Aucun réseau ajouté</div>}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    value={newNetwork}
                    onChange={(e) => setNewNetwork(e.target.value)}
                    className="form-control"
                    placeholder="Sélectionnez ou saisissez un regroupement stratégique"
                    list="network-suggestions"
                  />
                  <datalist id="network-suggestions">
                    {availableNetworks.map(network => (
                      <option key={network._id} value={network.name} />
                    ))}
                  </datalist>
                </div>
                
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={handleAddNetwork}
                  disabled={!newNetwork.trim()}
                >
                  <i className="fas fa-plus"></i> Ajouter
                </button>
              </div>
              <div className="mt-2 text-muted small">
                Vous pouvez sélectionner un regroupement existant ou en saisir un nouveau.
              </div>
            </div>
          </div>
          
          <div id="social" className="form-section">
            <h2 className="section-title">Réseaux sociaux</h2>
            <p className="section-description">Ajoutez des liens vers vos profils sur les réseaux sociaux.</p>
            
            <div className="array-items">
              {safeMap(formData?.socialMedia, (social, index) => (
                <div key={`social-${index}`} className="array-item">
                  <div className="item-content">
                    <div className="social-platform">
                      <i className={`fab fa-${social.platform.toLowerCase().replace('researchgate', 'researchgate').replace('x', 'twitter')}`}></i>
                      &nbsp;<strong>{social.platform}</strong>
                    </div>
                    {social.url && (
                      <a href={social.url} target="_blank" rel="noopener noreferrer" className="item-link">
                        <i className="fas fa-external-link-alt"></i> {social.url}
                      </a>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleObjectArrayRemove('socialMedia', index)}
                    aria-label={`Supprimer le réseau social ${social.platform}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )) || <div className="empty-message">Aucun réseau social ajouté</div>}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="social-platform">Plateforme</label>
                  <select
                    id="social-platform"
                    value={newSocialMedia.platform}
                    onChange={(e) => setNewSocialMedia({...newSocialMedia, platform: e.target.value})}
                    className="form-control"
                  >
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Facebook">Facebook</option>
                    <option value="X">X (Twitter)</option>
                    <option value="Instagram">Instagram</option>
                    <option value="ResearchGate">ResearchGate</option>
                    <option value="GitHub">GitHub</option>
                    <option value="YouTube">YouTube</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="social-url">URL du profil</label>
                  <input
                    id="social-url"
                    type="url"
                    placeholder="https://example.com/profile"
                    value={newSocialMedia.url}
                    onChange={(e) => setNewSocialMedia({...newSocialMedia, url: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => handleObjectArrayAdd('socialMedia', newSocialMedia, setNewSocialMedia, { platform: 'LinkedIn', url: '' })}
                disabled={!newSocialMedia.url.trim()}
              >
                <i className="fas fa-plus"></i> Ajouter le réseau social
              </button>
            </div>
          </div>
          
          <div id="projects" className="form-section">
            <h2 className="section-title">Projets de recherche</h2>
            <p className="section-description">Ajoutez vos projets de recherche, en indiquant s'ils sont financés par le RQM.</p>
            
            <div className="array-items">
              {safeMap(formData?.projects, (project, index) => (
                <div key={`project-${index}`} className="array-item">
                  <div className="item-content">
                    <div className="item-header">
                      <strong>{project.name}</strong>
                      {project.fundedByRQM && (
                         <span className="badge badge-rqm">
                          <i className="fas fa-award"></i> Financé par le RQM
                        </span>
                      )}
                    </div>
                    {project.description && <p className="item-description">{project.description}</p>}
                    {project.url && (
                      <a href={project.url} target="_blank" rel="noopener noreferrer" className="item-link">
                        <i className="fas fa-external-link-alt"></i> Site du projet
                      </a>
                    )}
                    {project.startDate && (
                      <div className="project-dates">
                        <span>Début: {new Date(project.startDate).toLocaleDateString()}</span>
                        {project.endDate && (
                          <span> - Fin: {new Date(project.endDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleObjectArrayRemove('projects', index)}
                    aria-label={`Supprimer le projet ${project.name}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )) || <div className="empty-message">Aucun projet de recherche ajouté</div>}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="project-name">Nom du projet</label>
                  <input
                    id="project-name"
                    type="text"
                    placeholder="Nom du projet de recherche"
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="project-description">Description du projet</label>
                  <textarea
                    id="project-description"
                    placeholder="Description du projet de recherche"
                    value={newProject.description || ''}
                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                    className="form-control"
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="project-url">URL du projet (optionnel)</label>
                  <input
                    id="project-url"
                    type="url"
                    placeholder="https://example.com/project"
                    value={newProject.url || ''}
                    onChange={(e) => setNewProject({...newProject, url: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <div className="form-row date-inputs">
                <div className="form-group">
                  <label htmlFor="project-start-date">Date de début (optionnel)</label>
                  <input
                    id="project-start-date"
                    type="date"
                    value={newProject.startDate ? new Date(newProject.startDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setNewProject({...newProject, startDate: e.target.value ? new Date(e.target.value) : null})}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="project-end-date">Date de fin (optionnel)</label>
                  <input
                    id="project-end-date"
                    type="date"
                    value={newProject.endDate ? new Date(newProject.endDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setNewProject({...newProject, endDate: e.target.value ? new Date(e.target.value) : null})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <input
                    id="project-rqm"
                    type="checkbox"
                    checked={newProject.fundedByRQM}
                    onChange={(e) => setNewProject({...newProject, fundedByRQM: e.target.checked})}
                    className="form-checkbox"
                  />
                  <label htmlFor="project-rqm">Projet financé par le RQM</label>
                </div>
              </div>
              
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => handleObjectArrayAdd('projects', newProject, setNewProject, { name: '', description: '', url: '', startDate: null, endDate: null, fundedByRQM: false })}
                disabled={!newProject.name.trim()}
              >
                <i className="fas fa-plus"></i> Ajouter le projet
              </button>
            </div>
          </div>
          
          <div id="datasets" className="form-section">
            <h2 className="section-title">Jeux de données</h2>
            <p className="section-description">Ajoutez des liens vers les jeux de données de vos recherches.</p>
            
            <div className="array-items">
              {safeMap(formData?.datasets, (dataset, index) => (
                <div key={`dataset-${index}`} className="array-item">
                  <div className="item-content">
  <strong>{dataset.name}</strong>
  {dataset.doi && (
    <span className="item-doi" style={{ display: 'block', fontSize: '0.95em', color: '#007199', marginTop: '2px' }}>
      DOI : <a href={`https://doi.org/${dataset.doi}`} target="_blank" rel="noopener noreferrer">{dataset.doi}</a>
    </span>
  )}
  {dataset.url && (
    <a href={dataset.url} target="_blank" rel="noopener noreferrer" className="item-link">
      &nbsp;<i className="fas fa-external-link-alt"></i> Accéder au jeu de données
    </a>
  )}
  {dataset.fundedByRQM && (
    <span className="rqm-funded-badge" style={{ display: 'inline-block', background: '#e6f7fa', color: '#007199', borderRadius: '4px', padding: '2px 8px', fontSize: '0.85em', marginLeft: '8px' }}>
      Campagne RQM
    </span>
  )}
  {dataset.description && <p className="item-description">{dataset.description}</p>}
</div>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleObjectArrayRemove('datasets', index)}
                    aria-label={`Supprimer le jeu de données ${dataset.name}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )) || <div className="empty-message">Aucun jeu de données ajouté</div>}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dataset-name">Nom du jeu de données</label>
                  <input
                    id="dataset-name"
                    type="text"
                    placeholder="Nom du jeu de données"
                    value={newDataset.name}
                    onChange={(e) => setNewDataset({...newDataset, name: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <div className="form-row">
  <div className="form-group">
    <label htmlFor="dataset-doi">DOI (identifiant numérique, optionnel)</label>
    <input
      id="dataset-doi"
      type="text"
      placeholder="ex: 10.1234/abcd.efgh"
      value={newDataset.doi || ''}
      onChange={(e) => setNewDataset({ ...newDataset, doi: e.target.value })}
      className="form-control"
    />
  </div>
</div>
<div className="form-row">
  <div className="form-group">
    <label htmlFor="dataset-url">URL du jeu de données</label>
    <input
      id="dataset-url"
      type="url"
      placeholder="https://example.com/dataset"
      value={newDataset.url}
      onChange={(e) => setNewDataset({...newDataset, url: e.target.value})}
      className="form-control"
    />
  </div>
</div>
<div className="form-row">
  <div className="form-group">
    <div className="checkbox-group" style={{ marginTop: '8px' }}>
      <input
        type="checkbox"
        id="dataset-fundedByRQM"
        checked={!!newDataset.fundedByRQM}
        onChange={e => setNewDataset({ ...newDataset, fundedByRQM: e.target.checked })}
      />
      <label htmlFor="dataset-fundedByRQM" style={{ marginLeft: '8px', fontWeight: 400 }}>
        Obtenue dans le cadre d’une campagne d’échantillonnage financée par le RQM
      </label>
    </div>
  </div>
</div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dataset-description">Description (optionnelle)</label>
                  <textarea
                    id="dataset-description"
                    placeholder="Description du jeu de données"
                    value={newDataset.description || ''}
                    onChange={(e) => setNewDataset({...newDataset, description: e.target.value})}
                    className="form-control"
                    rows="3"
                  />
                </div>
              </div>
              
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => handleObjectArrayAdd('datasets', newDataset, setNewDataset, { name: '', url: '', description: '' })}
                disabled={!newDataset.name.trim()}
              >
                <i className="fas fa-plus"></i> Ajouter le jeu de données
              </button>
            </div>
          </div>
          
          <div id="websites" className="form-section">
            <h2 className="section-title">Sites web</h2>
            <p className="section-description">Ajoutez vos sites web institutionnels ou personnels.</p>
            
            <div className="array-items">
              {safeMap(formData?.websites, (website, index) => (
                <div key={`website-${index}`} className="array-item">
                  <div className="item-content">
                    <div className="item-header">
                      <strong>{website.name}</strong>
                    </div>
                    {website.url && (
                      <a href={website.url} target="_blank" rel="noopener noreferrer" className="item-link">
                        <i className="fas fa-external-link-alt"></i> {website.url}
                      </a>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleObjectArrayRemove('websites', index)}
                    aria-label={`Supprimer le site web ${website.name}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )) || <div className="empty-message">Aucun site web ajouté</div>}
            </div>
            
            <div className="add-item-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="website-name">Nom du site</label>
                  <input
                    id="website-name"
                    type="text"
                    placeholder="Ex: Site institutionnel, Blog personnel"
                    value={newWebsite.name}
                    onChange={(e) => setNewWebsite({...newWebsite, name: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="website-url">URL du site</label>
                  <input
                    id="website-url"
                    type="url"
                    placeholder="https://example.com"
                    value={newWebsite.url}
                    onChange={(e) => setNewWebsite({...newWebsite, url: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => handleObjectArrayAdd('websites', newWebsite, setNewWebsite, { name: '', url: '' })}
                disabled={!newWebsite.name.trim() || !newWebsite.url.trim()}
              >
                <i className="fas fa-plus"></i> Ajouter le site web
              </button>
            </div>
          </div>
                   
          {/* Section CV */}
          <div id="cv" className="form-section">
            <h3>Curriculum Vitae (CV)</h3>
            <p className="section-description">
              Ajoutez votre CV sous forme d'URL ou téléversez un fichier PDF.
            </p>
            
            {formData.cv && formData.cv.url && (
  <div className="current-cv">
    <h4>CV actuel :</h4>
    <div className="cv-info" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {formData.cv.fileType === 'pdf' ? (
        <>
          <i className="fas fa-file-pdf"></i>
          &nbsp;<span>{formData.cv.fileName || 'CV.pdf'}</span>
          &nbsp;<button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => profileService.downloadCV(formData.cv.url)}
          >
            <i className="fas fa-download"></i> Télécharger
          </button>
        </>
      ) : (
        <>
          <i className="fas fa-external-link-alt"></i>
          <a href={formData.cv.url} target="_blank" rel="noopener noreferrer">
            {formData.cv.url}
          </a>
        </>
      )}
      <button
        type="button"
        className="btn btn-sm btn-danger"
        style={{ marginLeft: 16 }}
        onClick={handleRemoveCV}
      >
        <i className="fas fa-trash-alt"></i> Supprimer le CV
      </button>
      <div className="cv-date" style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
        Dernière mise à jour : {new Date(formData.cv.lastUpdated).toLocaleDateString()}
      </div>
    </div>
  </div>
)}
            
            <div className="cv-upload-options">
              <div className="form-group">
                <label>Type d'ajout :</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="cvUploadType"
                      value="url"
                      checked={cvUploadType === 'url'}
                      onChange={() => setCvUploadType('url')}
                    />
                    URL (lien vers votre CV en ligne)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="cvUploadType"
                      value="file"
                      checked={cvUploadType === 'file'}
                      onChange={() => setCvUploadType('file')}
                    />
                    Fichier PDF
                  </label>
                </div>
              </div>
              
              {cvUploadType === 'url' ? (
                <div className="form-group">
                  <label htmlFor="cv-url">URL de votre CV</label>
                  <input
                    id="cv-url"
                    type="url"
                    placeholder="https://example.com/mon-cv.pdf"
                    value={cvUrl}
                    onChange={(e) => setCvUrl(e.target.value)}
                    className="form-control"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="cv-file">Fichier PDF (max 10MB)</label>
                  <input
                    id="cv-file"
                    type="file"
                    accept="application/pdf"
                    onChange={handleCvFileChange}
                    className="form-control"
                  />
                  {cvFile && (
                    <div className="selected-file">
                      <i className="fas fa-file-pdf"></i> {cvFile.name}
                    </div>
                  )}
                </div>
              )}
              
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCvUpload}
                disabled={(cvUploadType === 'url' && !cvUrl) || (cvUploadType === 'file' && !cvFile)}
              >
                <i className="fas fa-upload"></i> {cvUploadType === 'url' ? 'Enregistrer l\'URL' : 'Téléverser le CV'}
              </button>
            </div>
          </div>
          
          {/* Section Documents */}
          <div id="documents" className="form-section">
            <h3>Documents</h3>
            <p className="section-description">
              Ajoutez des documents pertinents à votre profil (fichiers PDF ou liens externes).
            </p>
            
            {formData.documents && Array.isArray(formData.documents) && formData.documents.filter(doc => doc).length > 0 && (
              <div className="current-documents">
                <h4>Documents actuels :</h4>
                <div className="documents-list">
                  {formData.documents
                    .filter(doc => doc) // Filtrer les documents undefined
                    .map((document, index) => {
                    
                    return (
                      <div key={index} className="document-item">
                        <div className="document-info">
                          <h5 className="document-name">{document.name || 'Document sans nom'}</h5>
                          {document.url && (
                            document.fileType === 'pdf' ? (
                              <button 
                                onClick={() => profileService.downloadDocument(document.url)} 
                                className="btn btn-sm btn-outline"
                              >
                                <i className="fas fa-file-pdf"></i> Télécharger
                              </button>
                            ) : (
                              <a 
                                href={document.url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="document-link"
                              >
                                <i className="fas fa-external-link-alt"></i> {document.url.length > 30 ? document.url.substring(0, 30) + '...' : document.url}
                              </a>
                            )
                          )}
                          {document.description && (
                            <p className="document-description">{document.description}</p>
                          )}
                          {document.lastUpdated && (
                            <div className="document-date">
                              Dernière mise à jour : {new Date(document.lastUpdated).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <button 
                          type="button" 
                          className="btn btn-danger btn-sm" 
                          onClick={() => handleDocumentDelete(document._id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="document-upload-section">
              
              
              <div className="form-group">
                <label htmlFor="document-name">Nom du document</label>
                <input
                  id="document-name"
                  type="text"
                  placeholder="Nom du document"
                  value={newDocument.name}
                  onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="document-description">Description (optionnelle)</label>
                <textarea
                  id="document-description"
                  placeholder="Description du document"
                  value={newDocument.description}
                  onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                  className="form-control"
                  rows="2"
                />
              </div>
              
              <div className="document-upload-options">
                <div className="form-group">
                  <label>Type d'ajout :</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="documentUploadType"
                        value="url"
                        checked={documentUploadType === 'url'}
                        onChange={() => setDocumentUploadType('url')}
                      />
                      URL (lien vers un document en ligne)
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="documentUploadType"
                        value="file"
                        checked={documentUploadType === 'file'}
                        onChange={() => setDocumentUploadType('file')}
                      />
                      Fichier PDF
                    </label>
                  </div>
                </div>
                
                {documentUploadType === 'url' ? (
                  <div className="form-group">
                    <label htmlFor="document-url">URL du document</label>
                    <input
                      id="document-url"
                      type="url"
                      placeholder="https://example.com/mon-document.pdf"
                      value={documentUrl}
                      onChange={(e) => setDocumentUrl(e.target.value)}
                      className="form-control"
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label htmlFor="document-file">Fichier PDF (max 10MB)</label>
                    <input
                      id="document-file"
                      type="file"
                      accept="application/pdf"
                      onChange={handleDocumentFileChange}
                      className="form-control"
                    />
                    {documentFile && (
                      <div className="selected-file">
                        <i className="fas fa-file-pdf"></i> {documentFile.name}
                      </div>
                    )}
                  </div>
                )}
                
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleDocumentUpload}
                  disabled={
                    !newDocument.name || 
                    (documentUploadType === 'url' && !documentUrl) || 
                    (documentUploadType === 'file' && !documentFile)
                  }
                >
                  <i className="fas fa-upload"></i> {documentUploadType === 'url' ? 'Enregistrer l\'URL' : 'Téléverser le document'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="form-actions">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={openDeleteConfirmation}
              >
                <i className="fas fa-trash-alt"></i> Supprimer mon profil (Loi 25)
              </button>
              <button type="submit" className="btn btn-primary">
                Enregistrer les modifications
              </button>
            </div>
          </div>
        </form>

        {/* Boîte de dialogue de confirmation pour la suppression complète des données (Loi 25) */}
        {showDeleteConfirmation && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '5px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{ color: '#dc3545', marginBottom: '15px' }}>Confirmer la suppression complète (Loi 25)</h2>
              <p style={{ marginBottom: '15px' }}>
                <strong>ATTENTION:</strong> Vous êtes sur le point de supprimer <strong>DÉFINITIVEMENT</strong> toutes vos données 
                personnelles, y compris tous les fichiers associés à votre profil.
              </p>
              <p style={{ marginBottom: '15px' }}>
                Cette action est irréversible et est effectuée conformément à la Loi 25 du Québec sur la protection 
                des renseignements personnels.
              </p>
              {deleteError && (
                <div style={{ 
                  backgroundColor: '#f8d7da', 
                  color: '#721c24', 
                  padding: '10px', 
                  borderRadius: '5px', 
                  marginBottom: '15px' 
                }}>
                  {deleteError}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={closeDeleteConfirmation}
                  disabled={deleteLoading}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDeleteUserCompleteData}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Suppression en cours...' : 'Supprimer définitivement mes données'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {showScrollTop && (
        <button
          className="scroll-to-top-btn"
          onClick={scrollToTop}
          aria-label="Remonter en haut"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 9999,
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: 48,
            height: 48,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
        >
          <i className="fas fa-arrow-up"></i>
        </button>
      )}
    </div>
  );
};

export default ProfileSettings;
